import { useState } from 'react';
import { Image as ImageIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { mediaAbsoluteUrl } from '@/lib/media';
import { ImageUploadDialog } from './image-upload-dialog';

interface CoverImagePickerProps {
  /** Currently selected cover (null = none). */
  selected: { id: string; url: string; altText: string | null } | null;
  onChange: (next: { id: string; url: string; altText: string | null } | null) => void;
}

/**
 * Inline cover image control: shows current thumbnail + opens
 * ImageUploadDialog so editors can either upload a new file or pick from
 * the existing Media Library — same two-tab UI used by the post editor.
 */
export function CoverImagePicker({ selected, onChange }: CoverImagePickerProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <div className="space-y-2">
      {selected ? (
        <div className="relative overflow-hidden rounded-md border border-slate-200 dark:border-slate-800">
          <img
            src={mediaAbsoluteUrl(selected.url)}
            alt={selected.altText ?? 'Cover'}
            className="aspect-video w-full object-cover"
          />
          <button
            type="button"
            onClick={() => onChange(null)}
            aria-label="ลบรูปปก"
            className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white hover:bg-black/80"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="flex aspect-video w-full items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-500 hover:border-slate-400 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800"
        >
          <ImageIcon className="mr-2 h-4 w-4" />
          เลือกรูปปก
        </button>
      )}

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setPickerOpen(true)}
          className="flex-1"
        >
          {selected ? 'เปลี่ยนรูป' : 'อัปโหลด / เลือกจาก Media Library'}
        </Button>
      </div>

      <p className="text-[11px] text-slate-500">
        ใช้เป็นรูปแทนใน list + og:image สำหรับการแชร์ social
      </p>

      <ImageUploadDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        mode="cover"
        onPickMedia={(item) => onChange(item)}
      />
    </div>
  );
}
